import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface RegistrationFormProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  pin: string;
  ethAddress: string;
  receiveUpdates: boolean;
}

interface FormErrors {
  name?: string;
  email?: string;
  pin?: string;
  ethAddress?: string;
}

const EMAILJS_PUBLIC_KEY = '6DM4P6EXwzSX9sNSM';
const EMAILJS_SERVICE_ID = 'service_gip8jzn';
const EMAILJS_TEMPLATE_ID = 'template_l91ifwl';

const RegistrationForm: React.FC<RegistrationFormProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    pin: '',
    ethAddress: '',
    receiveUpdates: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation
    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    // PIN validation
    if (!formData.pin) {
      newErrors.pin = 'PIN is required';
    } else if (!/^\d{6}$/.test(formData.pin)) {
      newErrors.pin = 'PIN must be exactly 6 digits';
    }

    // ETH address validation
    if (!formData.ethAddress) {
      newErrors.ethAddress = 'ETH address is required';
    } else if (!/^0x[a-fA-F0-9]{40}$/.test(formData.ethAddress)) {
      newErrors.ethAddress = 'Invalid ETH address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(`${API_URL}/api/registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      console.log('Registration successful');
      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
        setFormData({
          name: '',
          email: '',
          pin: '',
          ethAddress: '',
          receiveUpdates: false
        });
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Registration failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Register for Pre-ITO</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {submitStatus === 'success' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Registration successful! Thank you for your interest.
            </Alert>
          )}
          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              An error occurred. Please try again later.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              name="name"
              label="Full Name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
            />
            <TextField
              name="pin"
              label="6-Digit PIN"
              type="text"
              value={formData.pin}
              onChange={handleChange}
              error={!!errors.pin}
              helperText={errors.pin}
              required
              fullWidth
              inputProps={{ maxLength: 6, pattern: '\\d{6}' }}
            />
            <TextField
              name="ethAddress"
              label="ETH Address"
              type="text"
              value={formData.ethAddress}
              onChange={handleChange}
              error={!!errors.ethAddress}
              helperText={errors.ethAddress}
              required
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="receiveUpdates"
                  checked={formData.receiveUpdates}
                  onChange={handleChange}
                  color="primary"
                />
              }
              label="I'd like to receive more information about Mummy & Daddy tokens"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            Register
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RegistrationForm;
